// Hooks para apps/expenses sin react-query (no está en deps).
// Estado local + refetch manual; suficiente para v0.1.

import { useCallback, useEffect, useState } from 'react';

import { expensesApi, ExpensesApiError } from '../api';
import type {
  CategoryCreateIn,
  DashboardSummary,
  Expense,
  ExpenseCategory,
  ExpenseCreateIn,
  ExpenseListFilters,
  ExpenseListResponse,
  ReceiptUploadResponse,
} from '../types';

function errorMessage(e: unknown): string {
  if (e instanceof ExpensesApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

// ── List ───────────────────────────────────────────────────────

export interface UseExpensesResult {
  data: ExpenseListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useExpenses(filters: ExpenseListFilters = {}): UseExpensesResult {
  const [data, setData] = useState<ExpenseListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filterKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await expensesApi.list(filters));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

// ── Categories ─────────────────────────────────────────────────

export interface UseCategoriesResult {
  data: ExpenseCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  create: (input: CategoryCreateIn) => Promise<ExpenseCategory | null>;
}

export function useCategories(): UseCategoriesResult {
  const [data, setData] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await expensesApi.listCategories());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (input: CategoryCreateIn) => {
    try {
      const cat = await expensesApi.createCategory(input);
      await refetch();
      return cat;
    } catch (e) {
      setError(errorMessage(e));
      return null;
    }
  }, [refetch]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch, create };
}

// ── Create / mutations ─────────────────────────────────────────

export interface UseCreateExpenseResult {
  submit: (data: ExpenseCreateIn) => Promise<Expense | null>;
  loading: boolean;
  error: string | null;
}

export function useCreateExpense(): UseCreateExpenseResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useCallback(async (data: ExpenseCreateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await expensesApi.create(data);
    } catch (e) {
      setError(errorMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  return { submit, loading, error };
}

export interface UseUploadReceiptResult {
  upload: (id: string, file: File) => Promise<ReceiptUploadResponse | null>;
  loading: boolean;
  error: string | null;
}

export function useUploadReceipt(): UseUploadReceiptResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = useCallback(async (id: string, file: File) => {
    setLoading(true);
    setError(null);
    try {
      return await expensesApi.uploadReceipt(id, file);
    } catch (e) {
      setError(errorMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  return { upload, loading, error };
}

// ── Dashboard ──────────────────────────────────────────────────

export interface UseDashboardResult {
  data: DashboardSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboard(): UseDashboardResult {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await expensesApi.dashboard());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refetch();
  }, [refetch]);
  return { data, loading, error, refetch };
}
